import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui-kit/badge';
import { Card } from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { cn } from '@/lib/utils';

/** Lists on Home show this many rows; the rest are behind the card's View link (Component Map › Card grid). */
export const HOME_LIST_ROWS = 4;

interface HomeCardProps {
  title: string;
  /** A small icon before the title (prototype `homeCard` `icon`). */
  icon?: ReactNode;
  /** The number of items, as an outline badge next to the title (attention, requests, approvals). */
  count?: number;
  /** The card's destination: a "View" link at the top right (Component Map › Home widget header). */
  viewTo?: string;
  /** Actions pinned to the card's bottom (e.g. Request time off). */
  footer?: ReactNode;
  className?: string;
  children: ReactNode;
}

/** A Home widget: header with title, count and View, the body, and a footer pinned to the bottom. */
export const HomeCard = ({
  title,
  icon,
  count,
  viewTo,
  footer,
  className,
  children,
}: HomeCardProps) => {
  const { t } = useTranslation();
  return (
    <Card className={cn('flex min-w-0 flex-col gap-3 p-4 shadow-none', className)}>
      <div className="flex items-center gap-2">
        {icon && <span className="shrink-0 text-muted-foreground [&_svg]:size-4">{icon}</span>}
        <h2 className="min-w-0 truncate text-base font-semibold">{title}</h2>
        {count !== undefined && count > 0 && (
          <Badge variant="outline" className="tabular-nums">
            {count}
          </Badge>
        )}
        {viewTo && (
          <Link
            to={viewTo}
            className="ml-auto shrink-0 text-sm font-medium text-primary hover:underline"
          >
            {t('VIEW')}
          </Link>
        )}
      </div>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      {footer && <div className="mt-auto flex flex-wrap gap-2 pt-1">{footer}</div>}
    </Card>
  );
};

type RowTone = 'default' | 'danger';

interface HomeListRowProps {
  icon: ReactNode;
  /** Red tint for overdue or missing days. */
  tone?: RowTone;
  title: ReactNode;
  meta?: ReactNode;
  /** An action button or a status badge. */
  end?: ReactNode;
  to?: string;
}

/** A row of a Home list: a 32px muted icon square, title and meta, and an action or badge on the right. */
export const HomeListRow = ({ icon, tone = 'default', title, meta, end, to }: HomeListRowProps) => {
  const body = (
    <>
      <span
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-md [&_svg]:size-4',
          tone === 'danger' ? 'bg-destructive-subtle text-destructive' : 'bg-muted text-muted-foreground'
        )}
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium">{title}</span>
        {meta && <span className="truncate text-xs text-muted-foreground">{meta}</span>}
      </span>
      {end && <span className="shrink-0">{end}</span>}
    </>
  );
  return (
    <li className="border-b last:border-b-0">
      {to ? (
        <Link
          to={to}
          className="-mx-1 flex items-center gap-3 rounded-md px-1 py-2 hover:bg-muted"
        >
          {body}
        </Link>
      ) : (
        <div className="flex items-center gap-3 py-2">{body}</div>
      )}
    </li>
  );
};

/** A list's body: rows, a placeholder while loading, or a short empty line. */
export const HomeList = ({
  isLoading,
  emptyText,
  children,
}: {
  isLoading?: boolean;
  emptyText: string;
  children: ReactNode[];
}) => {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 py-1">
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="h-8 w-full" />
        ))}
      </div>
    );
  }
  if (!children.length) return <p className="py-2 text-sm text-muted-foreground">{emptyText}</p>;
  return <ul className="flex flex-col">{children.slice(0, HOME_LIST_ROWS)}</ul>;
};
